import React, {useEffect, useMemo, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import styled from "styled-components";
import {postData} from "../../Utils/Server";
import Endpoints from "../../Constants/Endpoints";
import {updateDocumentTitle} from "../../Utils/Common";
import PageLayout from "../Layout/PageLayout";
import {Button, Card, CardTitleBar, DataTable, EmptyState, Spinner, StatCard, StatusBadge} from "../UI";
import {tokens} from "../../theme/tokens";
import {currentUser} from "../../Utils/Auth";
import {hasPermission, PERMS} from "../../Utils/Permissions";

const STATUS_THEME = {
    "OVERDUE": {
        bg: tokens.colors.status.danger.bg,
        accent: tokens.colors.status.danger.fg,
        bar: tokens.colors.status.danger.fg,
    },
    "NEARLY_LATE": {
        bg: tokens.colors.status.warn.bg,
        accent: tokens.colors.status.warn.fg,
        bar: tokens.colors.status.warn.fg,
    },
    "ON_TIME": {
        bg: tokens.colors.status.success.bg,
        accent: tokens.colors.status.success.fg,
        bar: tokens.colors.status.success.fg,
    },
    "NOT_STARTED": {
        bg: tokens.colors.surface,
        accent: tokens.colors.muted,
        bar: tokens.colors.faint,
    },
};

const themeFor = status => STATUS_THEME[status] || {
    bg: tokens.colors.surface,
    accent: tokens.colors.muted,
    bar: tokens.colors.faint,
};

const formatCountdown = (deadlineAt, nowMs) => {
    if (!deadlineAt) return {text: "—", overdue: false};
    const remainingMs = new Date(deadlineAt).getTime() - nowMs;
    const overdue = remainingMs <= 0;
    const mins = Math.trunc(Math.abs(remainingMs) / 60000);
    const secs = Math.trunc((Math.abs(remainingMs) % 60000) / 1000);
    const text = overdue
        ? `${mins}m ${String(secs).padStart(2, "0")}s overdue`
        : `${mins}m ${String(secs).padStart(2, "0")}s left`;
    return {text, overdue};
};

const TileGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: ${tokens.spacing[4]};
    margin-bottom: ${tokens.spacing[5]};
`;

const Updated = styled.span`
    display: inline-flex;
    align-items: center;
    gap: ${tokens.spacing[2]};
    font-size: ${tokens.typography.size.sm};
    font-weight: ${tokens.typography.weight.semibold};
    color: ${tokens.colors.muted};
`;

const TIMER_TONE = { OVERDUE: "danger", NEARLY_LATE: "warn", ON_TIME: "success", NOT_STARTED: "neutral" };

const windowMinutes = (deliveryType, expectedHours) =>
    expectedHours > 0 ? expectedHours * 60 : (String(deliveryType).toLowerCase() === "express" ? 60 : 120);

const localTimerStatus = (deadlineAt, windowMin, nowMs) => {
    if (!deadlineAt) return "NOT_STARTED";
    const mins = Math.trunc((new Date(deadlineAt).getTime() - nowMs) / 60000);
    if (mins <= 0) return "OVERDUE";
    return mins <= Math.max(10, Math.round(0.20 * windowMin)) ? "NEARLY_LATE" : "ON_TIME";
};

// live status for a row against the ticking clock
const rowStatus = (row, nowMs) => localTimerStatus(row.deadline_at, windowMinutes(row.delivery_type, row.request_type?.expected_time), nowMs);

const ReverseTimer = props => {
    let history = useHistory();
    const [isLoading, setIsLoading] = useState(false);
    const [activeList, setActiveList] = useState([]);
    const [lastUpdatedLabel, setLastUpdatedLabel] = useState("just now");
    const [now, setNow] = useState(Date.now());
    const lastLoadedRef = useRef(Date.now());

    const refreshLastUpdatedLabel = () => {
        const diffMs = Date.now() - lastLoadedRef.current;
        const diffMins = Math.floor(diffMs / 60000);
        const diffSecs = Math.floor((diffMs % 60000) / 1000);
        if (diffMins === 0 && diffSecs < 10) {
            setLastUpdatedLabel("just now");
        } else if (diffMins === 0) {
            setLastUpdatedLabel(`${diffSecs}s ago`);
        } else {
            setLastUpdatedLabel(`${diffMins} min${diffMins === 1 ? "" : "s"} ago`);
        }
    };

    const load = () => {
        let data = new FormData();
        setIsLoading(true);
        postData(Endpoints.REQUEST_TIMER, data)
            .then(res => res.json())
            .then(res => {
                setIsLoading(false);
                const next = Array.isArray(res.data) ? res.data : [];
                setActiveList(next);
                lastLoadedRef.current = Date.now();
                setLastUpdatedLabel("just now");
            })
            .catch((error) => {
                setIsLoading(false);
                console.log(error);
            });
    };

    useEffect(() => {
        updateDocumentTitle("Reverse Timer");
        load();

        const reloadId = setInterval(load, 60000);
        const tickId = setInterval(() => {
            setNow(Date.now());
            refreshLastUpdatedLabel();
        }, 1000);

        return () => {
            clearInterval(reloadId);
            clearInterval(tickId);
        };
    }, []);

    const counts = useMemo(() => ({
        overdue: activeList.filter(r => rowStatus(r, now) === "OVERDUE").length,
        nearly: activeList.filter(r => rowStatus(r, now) === "NEARLY_LATE").length,
        onTime: activeList.filter(r => rowStatus(r, now) === "ON_TIME").length,
    }), [activeList, now]);

    const columns = useMemo(() => [
        {
            name: "Code",
            sortable: true,
            selector: "request_code",
            grow: 0.7,
            cell: row => <span style={{fontSize: 14, fontWeight: 600}}>#{row.request_code}</span>,
        },
        {
            name: "Pick Up",
            grow: 1.6,
            cell: row => <span style={{fontSize: 14}}>{row.pick_up_location}</span>,
        },
        {
            name: "Start",
            selector: "start_time_string",
            grow: 0.9,
            cell: row => <span style={{fontSize: 13, color: tokens.colors.muted}}>{row.start_time_string}</span>,
        },
        {
            name: "Deadline",
            selector: "expected_end_time_string",
            grow: 0.9,
            cell: row => <span style={{fontSize: 13, color: tokens.colors.muted}}>{row.expected_end_time_string}</span>,
        },
        {
            name: "Status",
            selector: "status",
            grow: 0.7,
            cell: row => <span style={{fontSize: 13}}>{row.status}</span>,
        },
        {
            name: "Rider",
            grow: 1,
            cell: row => <span style={{fontSize: 13}}>{row.rider_details?.name || row.rider_id || "—"}</span>,
        },
        {
            name: "Type",
            grow: 0.8,
            cell: row => <span style={{fontSize: 13}}>{row.delivery_type}</span>,
        },
        {
            name: "Drop-off",
            grow: 1.6,
            cell: row => <span style={{fontSize: 13}}>{row.drop_offs?.[0]?.address || "—"}</span>,
        },
        {
            name: "Time Remaining",
            grow: 1.8,
            cell: row => {
                const theme = themeFor(rowStatus(row, now));
                const {text, overdue} = formatCountdown(row.deadline_at, now);
                const notStarted = !row.deadline_at;
                return (
                    <div style={{display: "flex", flexDirection: "column", width: "100%", padding: "6px 0"}}>
                        <span style={{
                            fontFamily: tokens.typography.fonts.mono,
                            fontSize: 16,
                            fontWeight: 700,
                            color: theme.accent,
                        }}>
                            {text}
                        </span>
                        {notStarted &&
                        <span style={{fontSize: 11, color: tokens.colors.muted, marginTop: 4}}>
                            not started
                        </span>
                        }
                        {!notStarted && overdue &&
                        <span style={{fontSize: 11, color: theme.accent, marginTop: 4, fontWeight: 600}}>
                            past deadline
                        </span>
                        }
                    </div>
                );
            },
        },
        {
            name: "Timer Status",
            grow: 1,
            cell: row => {
                const s = rowStatus(row, now);
                return <StatusBadge tone={TIMER_TONE[s]} status={s}>{s}</StatusBadge>;
            },
        },
    ], [now]);

    const conditionalRowStyles = [
        {
            when: row => rowStatus(row, now) === "OVERDUE",
            style: {
                backgroundColor: STATUS_THEME["OVERDUE"].bg,
                borderLeft: `4px solid ${STATUS_THEME["OVERDUE"].accent}`,
            },
        },
        {
            when: row => rowStatus(row, now) === "NEARLY_LATE",
            style: {
                backgroundColor: STATUS_THEME["NEARLY_LATE"].bg,
                borderLeft: `4px solid ${STATUS_THEME["NEARLY_LATE"].accent}`,
            },
        },
        {
            when: row => rowStatus(row, now) === "ON_TIME",
            style: {
                backgroundColor: STATUS_THEME["ON_TIME"].bg,
                borderLeft: `4px solid ${STATUS_THEME["ON_TIME"].accent}`,
            },
        },
    ];

    const renderTile = (label, value, accent) => (
        <StatCard
            label={label}
            value={<span style={{color: accent}}>{value}</span>}
            accent
        />
    );

    // Body.js does not wrap individual routes in <Guard>, so this page gates
    // itself: users without REQUEST_READ never see the timer data or table.
    if (!hasPermission(currentUser(), PERMS.REQUEST_READ)) {
        return (
            <PageLayout title="Reverse Timer">
                <EmptyState
                    title="Access denied"
                    description="You don't have permission to view the reverse timer."
                />
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title="Reverse Timer"
            actions={
                <>
                    <Updated>
                        {isLoading && <Spinner size={16}/>}
                        Last updated {lastUpdatedLabel}
                    </Updated>
                    <Button variant="outline" onClick={() => load()}>Reload</Button>
                </>
            }
        >
            <TileGrid>
                {renderTile("Overdue", counts.overdue, STATUS_THEME["OVERDUE"].accent)}
                {renderTile("Due soon", counts.nearly, STATUS_THEME["NEARLY_LATE"].accent)}
                {renderTile("On time", counts.onTime, STATUS_THEME["ON_TIME"].accent)}
            </TileGrid>

            <Card padded={false}>
                <CardTitleBar title="Ongoing requests, sorted by nearest to completion"/>

                {activeList.length === 0 && !isLoading &&
                <EmptyState title="No timed requests right now." description="Nothing is on the clock at the moment."/>
                }

                {activeList.length > 0 &&
                <DataTable
                    columns={columns}
                    data={activeList}
                    pointerOnHover={true}
                    highlightOnHover={true}
                    noHeader={true}
                    onRowClicked={e => {
                        history.push("/request/" + e.id);
                    }}
                    conditionalRowStyles={conditionalRowStyles}
                />
                }
            </Card>
        </PageLayout>
    );
};

export default ReverseTimer;
